import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { apiRequest } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type { Campaign, CampaignLocation, CampaignProduct, CampaignShift, ShiftSchedule } from '../../types/domain';

function CampaignSection<T extends { id: string }>({ title, resource, campaignId, children }: {
  title: string;
  resource: string;
  campaignId: string;
  children: (row: T) => ReactNode;
}) {
  const { accessToken } = useAuth();
  const [page, setPage] = useState(1);
  const query = useApiCollection<T>(`campaign-view-${resource}`, `/${resource}/?campaign=${campaignId}&page=${page}&page_size=25`, accessToken);
  return <section className="content-card management-panel">
    <h2>{title} <span className="record-count">{query.data?.count ?? 0}</span></h2>
    <DataPanel loading={query.isLoading} error={query.isError} empty={query.data?.count === 0}>
      <ul className="campaign-readonly-list">
        {query.data?.results.map((row) => <li key={row.id}>{children(row)}</li>)}
      </ul>
    </DataPanel>
    {(query.data?.count ?? 0) > 25 ? <div className="attendance-pagination">
      <span>Page {page} of {Math.ceil((query.data?.count ?? 0) / 25)}</span>
      <div>
        <button className="button secondary" disabled={!query.data?.previous || query.isFetching} onClick={() => setPage(page - 1)} type="button">Previous</button>
        <button className="button secondary" disabled={!query.data?.next || query.isFetching} onClick={() => setPage(page + 1)} type="button">Next</button>
      </div>
    </div> : null}
  </section>;
}

export function CampaignReadOnlyPage() {
  const { campaignId = '' } = useParams();
  const { accessToken } = useAuth();
  const query = useQuery({
    queryKey: ['campaign-view', campaignId, accessToken],
    enabled: Boolean(accessToken && campaignId),
    queryFn: () => apiRequest<Campaign>(`/campaigns/${campaignId}/`, accessToken ? { accessToken } : {}),
  });
  const campaign = query.data;
  return <>
    <header className="page-heading">
      <div><p className="eyebrow">Read-only campaign</p><h1>{campaign?.name ?? 'Campaign details'}</h1><p>View campaign locations, products and schedules. Changes are restricted to campaign managers.</p></div>
      <Link className="button secondary" to="/campaigns">Back to campaigns</Link>
    </header>
    <DataPanel loading={query.isLoading} error={query.isError} empty={!campaign}>
      {campaign ? <>
        <section className="content-card management-panel">
          <h2>{campaign.reference} <StatusPill value={campaign.status} /></h2>
          <p>{campaign.client_name} · {campaign.operating_agency_name ?? 'Direct client'}</p>
          <p>{campaign.starts_on} – {campaign.ends_on}</p>
          {campaign.description ? <p>{campaign.description}</p> : null}
        </section>
        <CampaignSection<CampaignLocation> key={`${campaignId}-locations`} campaignId={campaignId} resource="locations" title="Locations">
          {(row) => <><strong>{row.name}</strong><span>{row.address} · {row.city}</span></>}
        </CampaignSection>
        <CampaignSection<CampaignProduct> key={`${campaignId}-products`} campaignId={campaignId} resource="campaign-products" title="Products">
          {(row) => <><strong>{row.product_name}</strong><span>{row.product_sku} · {row.product_unit_price === null ? 'Price not set' : `PKR ${row.product_unit_price}`}</span>{row.target_value ? <span>Target: {row.target_value} {row.target_metric} · {row.target_period}</span> : null}</>}
        </CampaignSection>
        <CampaignSection<ShiftSchedule> key={`${campaignId}-schedules`} campaignId={campaignId} resource="shift-schedules" title="Shift schedules">
          {(row) => <><strong>{row.name} · {row.location_name}</strong><span>{row.starts_on} – {row.ends_on} · {row.timezone} · Revision {row.revision_number}</span>{row.rules.map((rule) => <span key={rule.id}>{rule.name}: {rule.weekdays.join(', ')} · {rule.start_time}–{rule.end_time}</span>)}</>}
        </CampaignSection>
        <CampaignSection<CampaignShift> key={`${campaignId}-shifts`} campaignId={campaignId} resource="shifts" title="Dated shifts">
          {(row) => <><strong>{row.location_name}</strong><span>{new Date(row.starts_at).toLocaleString('en-PK')} – {new Date(row.ends_at).toLocaleString('en-PK')}</span></>}
        </CampaignSection>
      </> : null}
    </DataPanel>
  </>;
}
